import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { ampli } from '@/ampli';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import Decimal from 'decimal.js-light';
import { type ChangeEvent, type ReactNode, useMemo, useState } from 'react';

import { isValidSlippageText } from '@/components/TransactionModule/utils';

import { useCheckoutMode } from '@/hooks/useCheckoutMode';

import {
  DEFAULT_SLIPPAGE_PERCENT as DEFAULT_SLIPPAGE_PERCENT_MARKET_MODE,
  SLIPPAGE_PRESETS as SLIPPAGE_PRESETS_MARKET_MODE,
  type SlippageMode,
} from '@/store/tradeSettings/market';

import { iosSafeInputText } from '@/styles/input';
import { fontStyles } from '@/styles/typography';

import { toAmpliSwapOrderType } from '@/utils/advancedOrderAnalytics';
import { SLIPPAGE_THRESHOLDS } from '@/utils/errors';

import { Section, SectionHeading, SectionBody } from '../../Section';

const AUTO_SLIPPAGE = 'auto';

interface SlippageProps {
  /** Selectable slippage presets */
  slippagePresets: Array<number | undefined>;
  /** Default slippage value */
  defaultSlippage: number;
  /** Slippage value in decimal format */
  slippage: number;
  /** Whether the slippage is custom */
  isSlippageCustom: boolean;
  /**  set slippage callback */
  setSlippage: (value: number, isCustom: boolean) => void;
  /** Section title */
  sectionTitle: string;
  /** Tooltip content */
  tooltipContent: ReactNode;
  /** Override the "Auto" toggle label (e.g. "Auto (1%)" for 0x mode). */
  autoLabel?: string;
  /** Current slippage mode. */
  slippageMode: SlippageMode;
  /** Called when the Auto toggle is selected. */
  onSelectAuto: () => void;
  /** Resolved Auto slippage in bps for the active pair; undefined until resolved. */
  autoSlippageBps?: number;
}

/**
 * Slippage settings section.
 */
const Slippage = ({
  slippagePresets,
  defaultSlippage,
  slippage,
  isSlippageCustom,
  setSlippage,
  sectionTitle,
  tooltipContent,
  autoLabel,
  slippageMode,
  onSelectAuto,
  autoSlippageBps,
}: SlippageProps) => {
  const isAutoMode = slippageMode === 'auto';

  // Derive "custom" from preset membership, not just the persisted flag: a stored
  // non-preset value (e.g. after a preset is removed) must still surface in the
  // custom input. Under Auto the stored value is the hidden manual fallback, so the
  // control is never "custom".
  const isCustomSlippage = !isAutoMode && (isSlippageCustom || !slippagePresets.includes(slippage));

  // state
  const [isSlippageValid, setIsSlippageValid] = useState(true);
  const [customSlippage, setCustomSlippage] = useState<string>(
    slippage && isCustomSlippage ? new Decimal(slippage).mul(100).toString() : '',
  );
  const [CheckoutMode] = useCheckoutMode();

  // vars
  const alert = useMemo(() => {
    // Slippage warning (manual only; Auto resolves a safe per-pair value, and its
    // hidden manual fallback should not trip the High/Low advice).
    if (!isAutoMode && slippage) {
      if (slippage >= SLIPPAGE_THRESHOLDS[1]) {
        return 'High Slippage settings';
      } else if (slippage < SLIPPAGE_THRESHOLDS[0]) {
        return 'Low Slippage settings';
      }
    }
    // Invalid slippage value
    if (!isSlippageValid) {
      return 'Invalid Slippage settings';
    }
  }, [isSlippageValid, slippage, isAutoMode]);

  // handlers
  const handleSlippageToggleValueChange = (value: string) => {
    // Prevent deselection when clicking the same value twice
    if (!value) return;

    setIsSlippageValid(true);
    const isAuto = value === AUTO_SLIPPAGE;
    if (isAuto) {
      onSelectAuto();
    } else {
      setSlippage(Number(value), false);
    }
    setCustomSlippage('');

    // Under Auto, report the pair-resolved percent when it is known; otherwise the
    // manual fallback that applies until Auto resolves.
    const slippagePercent = isAuto
      ? (autoSlippageBps ?? slippage * 10000) / 100
      : Number(value) * 100;
    ampli.slippageSettingUpdated({
      swap_slippage_percent: slippagePercent,
      swap_slippage_mode: isAuto ? 'auto' : 'manual',
      swap_order_type: toAmpliSwapOrderType(CheckoutMode),
      ui_location: 'settings_menu',
    });
  };

  const handleSlippageCustomInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!e.target.validity.valid) return;
    // allows 99.99 or less & no more than 2 digits past the decimal point
    if (value && Number(value) && new Decimal(value).greaterThan(100)) return;
    const regex = /^\d{0,2}(\.\d{0,2})?$/;
    if (value && !regex.test(value)) return;

    const normalizedValue = value.startsWith('.') ? `0${value}` : value;
    setCustomSlippage(normalizedValue);

    if (!normalizedValue) {
      setIsSlippageValid(true);
      setSlippage(defaultSlippage, false);
      return;
    }

    const isValid = isValidSlippageText(normalizedValue);
    setIsSlippageValid(isValid);
    if (!isValid) return;

    const numberValue = new Decimal(normalizedValue).div(100);
    setSlippage(numberValue.toNumber(), true);
  };

  const handleSlippageCustomInputBlur = () => {
    if (!customSlippage || !isSlippageValid) return;

    const slippagePercent = Number(customSlippage);
    ampli.slippageSettingUpdated({
      swap_slippage_percent: slippagePercent,
      swap_slippage_mode: 'manual',
      swap_order_type: toAmpliSwapOrderType(CheckoutMode),
      ui_location: 'settings_menu',
    });
  };

  const selectedSlippageValue = isCustomSlippage
    ? 'none'
    : isAutoMode
      ? AUTO_SLIPPAGE
      : String(slippage);

  return (
    <Section>
      <SectionHeading
        title={sectionTitle}
        className={fontStyles({
          variant: 'BODY_SM',
          class:
            'flex rounded-sm border-none shadow-20 py-2 px-3 max-w-[380px] transition-all ease-in-out duration-150 z-1000',
        })}
        alert={alert}
        isAltered={!isAutoMode}
      >
        <span className="w-[380px]">{tooltipContent}</span>
      </SectionHeading>

      <SectionBody>
        <ToggleGroup.Root
          className="flex flex-wrap w-full justify-between gap-2"
          type="single"
          value={selectedSlippageValue}
          onValueChange={handleSlippageToggleValueChange}
        >
          {slippagePresets.map((preset) => {
            return preset === undefined ? (
              <ToggleGroup.Item
                className={fontStyles({
                  variant: 'LABEL_MD',
                  class: [
                    'h-9',
                    'px-3',
                    'rounded-sm',
                    'border-2',
                    'border-transparent',
                    'bg-token-gray-200',
                    'text-token-gray-1600',
                    'hover:bg-special-background-button-medium-secondary-hover',
                    'focus:border-token-gray-600',
                    'focus:shadow-05',
                    'focus-visible:outline-token-gray-500',
                    'focus-visible:outline-[3px]',
                    'disabled:bg-token-alphalight-200',
                    'data-[state=on]:bg-special-background-button-medium-secondary',
                    'data-[state=on]:border-token-purple-900',
                    'data-[state=on]:shadow-05',
                    'w-full',
                    'basis-[30%]',
                    'grow',
                    'shrink',
                    'text-center',
                    'whitespace-nowrap',
                  ],
                })}
                key={AUTO_SLIPPAGE}
                value={AUTO_SLIPPAGE}
              >
                {autoLabel ?? 'Auto'}
              </ToggleGroup.Item>
            ) : (
              <ToggleGroup.Item
                className={fontStyles({
                  variant: 'LABEL_MD',
                  class: [
                    'h-9',
                    'px-3',
                    'rounded-sm',
                    'border-2',
                    'border-transparent',
                    'bg-token-gray-200',
                    'text-token-gray-1600',
                    'hover:bg-special-background-button-medium-secondary-hover',
                    'focus:border-token-gray-600',
                    'focus:shadow-05',
                    'focus-visible:outline-token-gray-500',
                    'focus-visible:outline-[3px]',
                    'disabled:bg-token-alphalight-200',
                    'data-[state=on]:bg-special-background-button-medium-secondary',
                    'data-[state=on]:border-token-purple-900',
                    'data-[state=on]:shadow-05',
                    'w-full',
                    'basis-[30%]',
                    'grow',
                    'shrink',
                    'text-center',
                  ],
                })}
                key={preset}
                value={String(preset)}
              >
                {preset * 100}%
              </ToggleGroup.Item>
            );
          })}

          <div className="relative flex-[1_1_30%]">
            <VisuallyHidden>
              <label htmlFor="slippageSetting">Custom Slippage Setting</label>
            </VisuallyHidden>

            <input
              id="slippageSetting"
              className={fontStyles({
                variant: 'LABEL_MD',
                class: [
                  'w-full',
                  'relative',
                  'min-w-[5.75rem]',
                  'h-9',
                  'px-3',
                  'rounded-sm',
                  'border-2',
                  customSlippage ? 'border-token-purple-900' : 'border-transparent',
                  'bg-token-gray-200',
                  'text-token-gray-1600',
                  'hover:bg-special-background-button-medium-secondary-hover',
                  'hover:cursor-text',
                  iosSafeInputText.md,
                  'outline-0',
                  customSlippage ? 'focus:border-token-purple-900' : 'focus:border-token-gray-600',
                  'focus:bg-special-background-button-medium-secondary',
                  'focus:shadow-05',
                ],
              })}
              value={customSlippage}
              onChange={handleSlippageCustomInputChange}
              onBlur={handleSlippageCustomInputBlur}
              placeholder="Custom"
              type="text"
              spellCheck="false"
              autoComplete="off"
              autoCorrect="off"
              pattern="^[0-9]*[.]?[0-9]*$"
            />
            <span className="text-token-gray-700 absolute right-2.5 top-[50%] transform translate-y-[-50%]">
              %
            </span>
          </div>
        </ToggleGroup.Root>
      </SectionBody>
    </Section>
  );
};

interface SlippageMarketModeProps {
  /** Slippage value in decimal format (the manual fallback value) */
  slippage: number;
  /**  whether the slippage is custom */
  isSlippageCustom: boolean;
  /**  set slippage callback (switches to manual mode) */
  setSlippage: (value: number, isCustom: boolean) => void;
  /**  tooltip content */
  tooltipContent: ReactNode;
  /** Current slippage mode; drives Auto vs preset/custom selection. */
  slippageMode: SlippageMode;
  /** Called when Auto is selected (switches to auto mode). */
  onSelectAuto: () => void;
  /** Dynamic "Auto · X%" label resolved from the active pair; plain "Auto" when absent. */
  autoLabel?: string;
  /** Resolved Auto slippage in bps for the active pair; undefined until resolved. */
  autoSlippageBps?: number;
}

export const SlippageMarketMode = ({
  slippage,
  isSlippageCustom,
  setSlippage,
  tooltipContent,
  slippageMode,
  onSelectAuto,
  autoLabel,
  autoSlippageBps,
}: SlippageMarketModeProps) => (
  <Slippage
    slippagePresets={[undefined, ...SLIPPAGE_PRESETS_MARKET_MODE]}
    defaultSlippage={DEFAULT_SLIPPAGE_PERCENT_MARKET_MODE}
    slippage={slippage}
    isSlippageCustom={isSlippageCustom}
    setSlippage={setSlippage}
    sectionTitle="Max. slippage"
    tooltipContent={tooltipContent}
    slippageMode={slippageMode}
    onSelectAuto={onSelectAuto}
    autoLabel={autoLabel}
    autoSlippageBps={autoSlippageBps}
  />
);
